<script>
    import TheSvgIcon from "@main/packages/TheSvgIcon";

    export default {
        name: "ThePagination",
        components: {
            TheSvgIcon,
        },
        props: {
            value: {
                type: Number,
            },
            last: {
                type: Number,
                required: true,
            },
            current: {
                type: Number,
            },
            pageRange: {
                type: Number,
                default: 3,
            },
            marginPages: {
                type: Number,
                default: 1,
            },
            isArrow: {
                type: Boolean,
                default: true,
            },
        },

        data() {
            return {
                innerValue: 1,
            };
        },
        watch: {
            current(value) {
                this.selected = value;
            },
        },
        computed: {
            selected: {
                get() {
                    return this.value || this.innerValue;
                },
                set(value) {
                    this.innerValue = value;
                },
            },
            pages() {
                let items = {};
                if (this.last <= this.pageRange) {
                    for (let index = 0; index < this.last; index++) {
                        items[index] = {
                            index: index,
                            content: index + 1,
                            selected: index === this.selected - 1,
                        };
                    }
                } else {
                    const halfPageRange = Math.floor(this.pageRange / 2);

                    let setPageItem = (index) => {
                        items[index] = {
                            index: index,
                            content: index + 1,
                            selected: index === this.selected - 1,
                        };
                    };

                    let setBreakView = (index) => {
                        items[index] = {
                            disabled: true,
                            breakView: true,
                        };
                    };

                    for (let i = 0; i < this.marginPages; i++) {
                        setPageItem(i);
                    }

                    let selectedRangeLow = 0;
                    if (this.selected - halfPageRange > 0) {
                        selectedRangeLow = this.selected - 1 - halfPageRange;
                    }

                    let selectedRangeHigh = selectedRangeLow + this.pageRange - 1;
                    if (selectedRangeHigh >= this.last) {
                        selectedRangeHigh = this.last - 1;
                        selectedRangeLow = selectedRangeHigh - this.pageRange + 1;
                    }

                    for (
                        let i = selectedRangeLow;
                        i <= selectedRangeHigh && i <= this.last - 1;
                        i++
                    ) {
                        setPageItem(i);
                    }

                    if (selectedRangeLow > this.marginPages) {
                        setBreakView(selectedRangeLow - 1);
                    }

                    if (selectedRangeHigh + 1 < this.last - this.marginPages) {
                        setBreakView(selectedRangeHigh + 1);
                    }

                    for (let i = this.last - 1; i >= this.last - this.marginPages; i--) {
                        setPageItem(i);
                    }
                }
                return items;
            },
            firstPageSelected() {
                return this.selected === 1;
            },
            lastPageSelected() {
                return this.selected === this.last || this.last === 0;
            },
        },
        created() {
            if (this.current === undefined) return;
            if (this.current !== this.selected) {
                this.selected = this.current;
            }
        },
        methods: {
            onChangePage(selected) {
                if (this.selected === selected) return;
                this.innerValue = selected;
                this.$emit("on-change", selected);
            },
            prevPage() {
                if (this.selected <= 1) return;
                this.onChangePage(this.selected - 1);
            },
            nextPage() {
                if (this.selected >= this.last) return;
                this.onChangePage(this.selected + 1);
            },
        },
    };
</script>

<template>
    <div class="the-pagination">
        <button
            v-if="isArrow"
            class="the-pagination__arrow the-pagination__arrow--prev outline"
            @click="prevPage"
            @keyup.enter="prevPage"
            :disabled="firstPageSelected"
        >
            <the-svg-icon name="arrow"></the-svg-icon>
        </button>

        <template v-for="page in pages">
            <button
                v-if="page.breakView"
                :key="page.index"
                class="the-pagination__button outline"
                :class="{ 'is-disabled': page.disabled }"
            >
                ...
            </button>
            <button
                v-else-if="page.disabled"
                :key="page.index"
                class="the-pagination__button outline is-disabled"
                :class="{ 'is-active': page.selected }"
            >
                {{ page.content }}
            </button>
            <button
                v-else
                :key="page.index"
                class="the-pagination__button outline"
                @click="onChangePage(page.index + 1)"
                @keyup.enter="onChangePage(page.index + 1)"
                :class="{ 'is-active': page.selected }"
            >
                {{ page.content }}
            </button>
        </template>

        <button
            v-if="isArrow"
            class="the-pagination__arrow the-pagination__arrow--next outline"
            @click="nextPage"
            @keyup.enter="nextPage"
            :disabled="lastPageSelected"
        >
            <the-svg-icon name="arrow"></the-svg-icon>
        </button>
    </div>
</template>

<style lang="scss">
    .the-pagination {
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .the-pagination__button {
        @include box(var(--pagination-inner-size));
        margin: 0 2px;
        color: var(--pagination-text-color);
        font-size: var(--pagination-text-size);
        font-weight: var(--pagination-text-weigth);
        line-height: var(--pagination-inner-size);
        text-align: center;
        border-radius: var(--pagination-button-radius);
        user-select: none;
        transition: color 0.2s ease-in, background-color 0.2s ease-in, text-shadow 0.2s ease-in;
        &:disabled {
            cursor: auto;
        }
        &.is-active {
            background-color: var(--pagination-button-color);
            color: var(--pagination-text-color--active);
            text-shadow: 0 0 1px #fff;
            pointer-events: none;
        }
        @include bp($bp-desktop-sm) {
            line-height: 40px;
            &:hover:not(.is-active):not(.is-disabled) {
                color: var(--pagination-text-color--hover);
            }
        }
    }

    .the-pagination__arrow {
        display: flex;
        justify-content: center;
        align-items: center;
        @include box(var(--pagination-inner-size));
        &:disabled {
            pointer-events: none;
            svg {
                fill: #b2b2c5;
            }
        }
        svg {
            fill: var(--pagination-arrow-color);
            @include box(16px);
            @include bp($bp-desktop-sm) {
                @include box(20px);
            }
        }
    }

    .the-pagination__arrow--prev {
        margin-right: 5px;
    }

    .the-pagination__arrow--next {
        margin-left: 5px;
        svg {
            transform: scale(-1);
        }
    }
</style>
